import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  InputBase,
  Menu,
  MenuItem,
  TableSortLabel,
} from '@mui/material';
import { MoreVert, Undo, Redo, TableRows, TableChart } from '@mui/icons-material';
import { useUndoRedo } from '../../hooks/useUndoRedo';
import { useEstimateData } from '../../contexts/EstimateDataContext';
import AddTesSectionDialog from './AddTesSectionDialog';
import { TesSectionApi } from '../../services/estimateService';
import { Estimate } from '../../pages/EstimatesPage';
import { getSectionForNr, TesSection, compareSectionNr, findBestSection } from '../../utils/sectionUtils';

interface SimpleEstimateTabProps {
  estimate: Estimate;
  positions: any[];
}

interface SimplifiedPosition {
  id: number;
  nr: string;
  type: 'S' | 'U';
  basis: string;
  description: string;
  unit: string;
  quantity: number;
  unitPrice: number;
  value: number;
}

// Generuj placeholdery bazując na logice DetailedEstimateTab
export const generateSampleSimplifiedPositions = (): SimplifiedPosition[] => {
  const bases = ['KNR 2-02 0101', 'KNNR 4-01 0202', 'KNR 5-05 0303', 'KNR 7-03 0104'];
  const descs = [
    'Roboty ziemne – wykopy ręczne\nGłębokość do 1,5 m',
    'Fundamenty betonowe B20',
    'Strop Teriva I 30/51',
    'Murowanie ścian z bloczków silikatowych',
  ];
  return Array.from({ length: 100 }).map((_, i) => {
    const qty = parseFloat((Math.random() * 900 + 20).toFixed(3));
    const up = parseFloat((Math.random() * 300 + 50).toFixed(2));
    return {
      id: i + 1,
      nr: (i + 1).toString(),
      type: (i % 3 === 0 ? 'U' : 'S'),
      basis: bases[i % bases.length],
      description: descs[i % descs.length],
      unit: 'm2',
      quantity: qty,
      unitPrice: up,
      value: parseFloat((qty * up).toFixed(2)),
    };
  });
};

const SimpleEstimateTab: React.FC<SimpleEstimateTabProps> = ({ estimate, positions = [] }) => {
  // Jeśli pozycje mają children zamiast rms, zmapuj:
  const localPositions = positions.map(pos => ({
    ...pos,
    rms: pos.children || pos.rms || []
  }));
  const { state: rows, setState: setRows, undo, redo, canUndo, canRedo } = useUndoRedo<SimplifiedPosition[]>([]);
  const [tesDialogOpen, setTesDialogOpen] = useState(false);
  const [editingCell, setEditingCell] = useState<{ id: number; field: string; value: string } | null>(null);

  // Ładuj pozycje uproszczone z przekazanego props localPositions
  useEffect(() => {
    if (localPositions && localPositions.length) {
      const converted = localPositions.map(p => {
        const qty = parseFloat(p.quantity) || 0;
        // Jeśli pozycja szczegółowa – policz cenę jedn. z RMS (R/M/S + Kp/Kz/Z)
        const rmsArr = (p.rms || []) as any[];
        const sumR = rmsArr.filter((r:any)=>r.rms_type==='R').reduce((s:number,r:any)=> s + (parseFloat(r.norm)||0)*(parseFloat(r.rms_price)||0), 0);
        const sumM = rmsArr.filter((r:any)=>r.rms_type==='M').reduce((s:number,r:any)=> s + (parseFloat(r.norm)||0)*(parseFloat(r.rms_price)||0), 0);
        const sumS = rmsArr.filter((r:any)=>r.rms_type==='S').reduce((s:number,r:any)=> s + (parseFloat(r.norm)||0)*(parseFloat(r.rms_price)||0), 0);
        let kpUnit = 0, kzUnit = 0, zUnit = 0;
        const kp = rmsArr.find((r:any)=>r.rms_type==='Kp');
        if(kp) kpUnit = (parseFloat(kp.norm)||0)/100 * sumR;
        const kz = rmsArr.find((r:any)=>r.rms_type==='Kz');
        if(kz) kzUnit = (parseFloat(kz.norm)||0)/100 * sumM;
        const z  = rmsArr.find((r:any)=>r.rms_type==='Z');
        if(z)  zUnit  = (parseFloat(z.norm)||0)/100 * (sumR+sumM+sumS+kpUnit+kzUnit);
        const detailedUnit = sumR+sumM+sumS+kpUnit+kzUnit+zUnit;
        const unitPrice = p.position_type==='U' ? (parseFloat(p.unit_price)||0) : detailedUnit;
        return {
          id: p.id,
          nr: (p.seq_no !== undefined ? p.seq_no.toString() : p.id.toString()),
          type: p.position_type,
          basis: (p.nr || '').toString().split(' ').slice(0, 3).join(' '),
          description: p.description,
          unit: p.unit,
          quantity: qty,
          unitPrice,
          value: unitPrice * qty
        } as SimplifiedPosition;
      });
      setRows(() => converted);
    } else {
      setRows(()=>[]);
    }
  }, [localPositions, setRows]);
  const { setSectionData, data: ctxData } = useEstimateData();
  const tesRows = (ctxData?.tesRows || []) as TesSection[];
  const [search, setSearch] = useState('');
  const handleTesSaved = (sec: TesSectionApi) => {
    const prevRows = ctxData?.tesRows || [];
    const idx = prevRows.findIndex((s:any)=>s.id===sec.id);
    const newRows = idx>-1 ? prevRows.map((s:any)=>s.id===sec.id?sec:s) : [...prevRows, sec];
    setSectionData('tesRows', newRows.sort((a:any,b:any)=>compareSectionNr(a.nr,b.nr)));
  };
  const [sectionFilter, setSectionFilter] = useState<string>('all');
  const [groupView, setGroupView] = useState<boolean>(true);  // true = z działami
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  // Sortowanie
  const [sortField, setSortField] = useState<'nr'|'description'|'quantity'|'value'>('nr');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('asc');

  const handleSort = (field:'nr'|'description'|'quantity'|'value')=>{
    if(sortField===field){ setSortDir(prev=> prev==='asc'?'desc':'asc'); }
    else { setSortField(field); setSortDir('asc'); }
  };

  // global undo/redo shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (canUndo) undo();
      }
      if (((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'y') || ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'z')) {
        e.preventDefault();
        if (canRedo) redo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [canUndo, canRedo, undo, redo]);

  // skrót "d" – otwarcie dialogu
  useEffect(()=>{
    const h=(e:KeyboardEvent)=>{
      if(e.key.toLowerCase()==='d' && !e.metaKey && !e.ctrlKey && !e.altKey){
        const tag=(e.target as HTMLElement).tagName;
        if(tag==='INPUT'||tag==='TEXTAREA'||(e.target as HTMLElement).isContentEditable) return;
        e.preventDefault();
        setTesDialogOpen(true);
      }
    };
    window.addEventListener('keydown',h);
    return ()=>window.removeEventListener('keydown',h);
  },[]);

  useEffect(() => { setSectionData('simplifiedRows', rows); }, [rows, setSectionData]);

  // Filtr tekstowy + filtr działu
  const filteredRows = useMemo(()=>{
    let tmp = rows.filter(r =>
      r.nr.includes(search) ||
      r.basis.toLowerCase().includes(search.toLowerCase()) ||
      r.description.toLowerCase().includes(search.toLowerCase())
    );
    if(sectionFilter !== 'all'){
      tmp = tmp.filter(r => getSectionForNr(r.nr, tesRows)?.id.toString() === sectionFilter);
    }
    return tmp;
  }, [rows, search, sectionFilter, tesRows]);

  const sortedRows = useMemo(()=>{
    const arr=[...filteredRows];
    arr.sort((a,b)=>{
      let av:any, bv:any;
      switch(sortField){
        case 'nr': av=parseInt(a.nr); bv=parseInt(b.nr); break;
        case 'description': av=a.description.toLowerCase(); bv=b.description.toLowerCase(); break;
        case 'quantity': av=a.quantity; bv=b.quantity; break;
        case 'value': av=a.value; bv=b.value; break;
      }
      if(av<bv) return sortDir==='asc'?-1:1;
      if(av>bv) return sortDir==='asc'?1:-1;
      return 0;
    });
    return arr;
  }, [filteredRows, sortField, sortDir]);

  // Grupowanie wg działów (tylko gdy wybrano "all")
  const rowsWithHeaders = useMemo(()=>{
    if(!groupView) return sortedRows.map(r=>({type:'row',data:r}));
    // Fallback: jeśli brak TES – pokaż pozycje bez nagłówków
    if(!tesRows || tesRows.length===0) return sortedRows.map(r=>({type:'row',data:r}));
    const res:any[]=[];
    const sortedSecs=[...tesRows].sort((a,b)=>compareSectionNr(a.nr,b.nr));
    sortedSecs.forEach(sec=>{
      const inSec = sortedRows.filter(r=>{
        const best = findBestSection(parseInt(r.nr), tesRows);
        return best?.id===sec.id;
      });
      res.push({type:'header',section:sec,total: inSec.reduce((s,x)=>s+x.value,0)});
      inSec.forEach(r=> res.push({type:'row',data:r}));
    });
    return res;
  },[sortedRows,groupView,tesRows]);

  const tableContainerRef = useRef<HTMLDivElement>(null);

  useEffect(()=>{
    tableContainerRef.current?.focus();
  },[]);

  const scrollRowIntoView = (id: number) => {
    const rowEl = document.querySelector(`[data-rowid="row-${id}"]`) as HTMLElement | null;
    const container = tableContainerRef.current;
    if (rowEl && container) {
      const bottom = rowEl.offsetTop + rowEl.offsetHeight;
      if (bottom > container.scrollTop + container.clientHeight) container.scrollTop = bottom - container.clientHeight;
      if (rowEl.offsetTop < container.scrollTop) {
        const headerH = (container.querySelector('thead') as HTMLElement)?.offsetHeight || 0;
        container.scrollTop = rowEl.offsetTop - headerH;
      }
    }
  };

  // Menu
  const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>, rowId: number) => {
    setMenuAnchorEl(e.currentTarget);
    setSelectedRowId(rowId);
  };
  const handleMenuClose = () => setMenuAnchorEl(null);

  const handleGotoDetailed = () => {
    // Placeholder – w przyszłości wywoła callback do EstimateDetails
    handleMenuClose();
  };

  // Keyboard navigation ograniczona do wyboru wiersza
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if(!selectedRowId && (e.key==='ArrowDown'||e.key==='ArrowUp')){
      const container = tableContainerRef.current;
      if(container){
        const rowEl = container.querySelector('tr[data-rowid]') as HTMLElement | null;
        if(rowEl){
          const id = parseInt(rowEl.dataset.rowid!.split('-')[1],10);
          setSelectedRowId(id);
        }
      }
      return;
    }
    const rowsEls = Array.from(document.querySelectorAll('[data-rowid]')) as HTMLElement[]; // header rows nie mają data-rowid
    const idx = rowsEls.findIndex(el => el.dataset.rowid === `row-${selectedRowId}`);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = rowsEls[idx + 1];
      if (next) {
        const id = parseInt(next.dataset.rowid!.split('-')[1]);
        setSelectedRowId(id);
        scrollRowIntoView(id);
      }
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = rowsEls[idx - 1];
      if (prev) {
        const id = parseInt(prev.dataset.rowid!.split('-')[1]);
        setSelectedRowId(id);
        scrollRowIntoView(id);
      }
    }
    if ((e.key === 'w' || e.key === 'W') && !editingCell && document.activeElement?.tagName !== 'INPUT') {
      e.preventDefault();
      setGroupView(prev => !prev);
    }
  };

  return (
    <Box sx={{ height: '100%', outline: 'none' }} tabIndex={0} onKeyDown={handleKeyDown}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, overflow: 'visible' }}>
        <Tooltip title="Cofnij (⌘Z)"><span><IconButton size="small" onClick={undo} disabled={!canUndo}><Undo fontSize="small"/></IconButton></span></Tooltip>
        <Tooltip title="Ponów (⌘Y)"><span><IconButton size="small" onClick={redo} disabled={!canRedo}><Redo fontSize="small"/></IconButton></span></Tooltip>
        <Tooltip title="Dodaj dział"><IconButton size="small" onClick={()=>setTesDialogOpen(true)}>D</IconButton></Tooltip>
        <Tooltip title="Dodaj pozycję"><IconButton size="small" onClick={()=>console.log('Dodaj pozycję')}>P</IconButton></Tooltip>

        {/* Przełącznik widoku działów */}
        <Tooltip title={groupView ? 'Widok bez działów' : 'Widok z działami'}>
          <IconButton size="small" onClick={()=>setGroupView(prev=>!prev)}>
            {groupView ? <TableRows fontSize="small"/> : <TableChart fontSize="small"/>}
          </IconButton>
        </Tooltip>

        {/* Kontener wyrównany do prawej */}
        <Box sx={{ display:'flex', alignItems:'center', gap:1, ml:'auto' }}>
          {/* Filtr działu */}
          <TextField
            select
            size="small"
            value={sectionFilter}
            onChange={e => setSectionFilter(e.target.value)}
            sx={{ minWidth: 220, overflow: 'visible', '& .MuiOutlinedInput-root': { overflow: 'visible' } }}
            onKeyDown={e => {
              e.stopPropagation();
              if (e.key === 'Escape') {
                tableContainerRef.current?.focus();
              }
            }}
          >
            <MenuItem value="all">Wszystkie działy</MenuItem>
            {tesRows.map(sec=> (
              <MenuItem key={sec.id} value={sec.id.toString()}>{sec.nr} – {sec.name}</MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            placeholder="Search…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ width: 220 }}
            onKeyDown={e => {
              e.stopPropagation();
              if (e.key === 'Escape') {
                tableContainerRef.current?.focus();
              }
            }}
          />
        </Box>
        <AddTesSectionDialog
          open={tesDialogOpen}
          estimateId={estimate.id}
          onClose={()=>setTesDialogOpen(false)}
          onSaved={handleTesSaved}
          lastPositionNr={localPositions.length > 0 ? localPositions[localPositions.length - 1].nr : undefined}
        />
      </Box>

      {/* Table */}
      <TableContainer component={Box} ref={tableContainerRef} tabIndex={0} sx={{ maxHeight:'calc(100vh - 200px)', overflowY:'auto', borderRadius:0, '&:focus':{outline:'none'} }}>
        <Table stickyHeader size="small" sx={{
          tableLayout: 'auto',
          fontSize: '0.70rem',
          '& .MuiTableCell-root': { padding: '3px 6px' },
          '& thead .MuiTableCell-root': { fontSize: '0.78rem', fontWeight: 600 }
        }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width:70,cursor:'pointer' }} onClick={()=>handleSort('nr')}>
                <TableSortLabel active={sortField==='nr'} direction={sortDir}>Nr</TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: 30 }}>T</TableCell>
              <TableCell sx={{ width:140 }}>Podstawa</TableCell>
              <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word', cursor:'pointer' }} onClick={()=>handleSort('description')}>
                <TableSortLabel active={sortField==='description'} direction={sortDir}>Opis</TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: 70 }}>J.m.</TableCell>
              <TableCell align="right" sx={{ width:110,cursor:'pointer' }} onClick={()=>handleSort('quantity')}>
                <TableSortLabel active={sortField==='quantity'} direction={sortDir}>Ilość</TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ width: 110 }}>Cena jedn.</TableCell>
              <TableCell align="right" sx={{ width:120,cursor:'pointer' }} onClick={()=>handleSort('value')}>
                <TableSortLabel active={sortField==='value'} direction={sortDir}>Wartość</TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ width: 40 }}>A</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsWithHeaders.map((item, idx)=>{
              if(item.type==='header' && item.section){
                return (
                  <TableRow key={`hdr-${item.section.id}`} sx={{ backgroundColor:'#e0e0e0' }}>
                    <TableCell colSpan={7} sx={{ fontWeight:700 }}>
                      {`${(item.section.nr || '').replace(/\.+$/, '')}. ${item.section.name}`}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight:700 }}>
                      {item.total!.toLocaleString(undefined,{minimumFractionDigits:2})}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                );
              }
              const row = item.data!;
              return (
              <TableRow
                key={row.id}
                tabIndex={0}
                data-rowid={`row-${row.id}`}
                selected={selectedRowId === row.id}
                onClick={() => setSelectedRowId(row.id)}
                sx={selectedRowId === row.id ? { outline: '2px solid #1a73e8', outlineOffset: -2 } : {}}
              >
                <TableCell>{row.nr}</TableCell>
                <TableCell>{row.type}</TableCell>
                <TableCell>{row.basis}</TableCell>
                <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.description}</TableCell>
                <TableCell>{row.unit}</TableCell>
                <TableCell align="right">{row.quantity.toFixed(3)}</TableCell>
                <TableCell align="right">{row.unitPrice.toFixed(2)}</TableCell>
                <TableCell align="right">{row.value.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={e => handleMenuOpen(e, row.id)}>
                    <MoreVert fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={handleGotoDetailed}>Idź do poz. szczegółowej</MenuItem>
      </Menu>
    </Box>
  );
};

export default SimpleEstimateTab;
